import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dropdown, Avatar, Space, Badge } from 'antd';
import {
  UserOutlined,
  SettingOutlined,
  LockOutlined,
  LogoutOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { AuthContext } from '../../context/AuthContext';
import { getRoleLabel } from '../../utils/rolePermissions';
import AccountSettingsModal from './AccountSettingsModal';
import ChangePasswordModal from './ChangePasswordModal';
import logger from '../../utils/logger';

const AccountDropdown = () => {
  const { currentUser, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const [accountSettingsOpen, setAccountSettingsOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const handleLogout = () => {
    logger.debug('🚪 USER CLICKED LOGOUT - should clear memory');
    logout(null, true); // Voluntary logout - clear memory
    navigate('/login');
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin':
        return 'var(--rlc-surface-inverse)'; // dark teal
      case 'tenant_admin':
        return 'var(--rlc-state-info)'; // blue
      default:
        return 'var(--rlc-state-success)'; // green
    }
  };

  // Build menu items
  const items = [
    {
      key: 'user-info',
      type: 'group',
      label: (
        <div style={{ padding: '8px 0' }}>
          <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--brand-darkblue)' }}>
            {currentUser?.first_name} {currentUser?.last_name}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--rlc-text-secondary)', marginTop: '2px' }}>
            {currentUser?.email}
          </div>
          <div style={{ marginTop: '6px' }}>
            <Badge
              color={getRoleBadgeColor(currentUser?.role)}
              text={
                <span style={{ fontSize: '11px', fontWeight: 500 }}>
                  {getRoleLabel(currentUser?.role)}
                </span>
              }
            />
          </div>
          {currentUser?.tenant_id && (
            <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid var(--rlc-surface-muted)' }}>
              <div style={{ fontSize: '11px', color: 'var(--rlc-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Organization
              </div>
              <div style={{ fontSize: '12px', color: 'var(--rlc-text-secondary)', marginTop: '2px', fontWeight: 500 }}>
                {currentUser?.tenant_name || 'Unknown'}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--rlc-text-muted)', marginTop: '1px', fontFamily: 'monospace' }}>
                {currentUser?.tenant_id}
              </div>
            </div>
          )}
        </div>
      ),
    },
    {
      type: 'divider',
    },
    {
      key: 'account-settings',
      icon: <SettingOutlined />,
      label: 'Account Settings',
      onClick: () => setAccountSettingsOpen(true),
    },
    {
      key: 'change-password',
      icon: <LockOutlined />,
      label: 'Change Password',
      onClick: () => setChangePasswordOpen(true),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
      danger: true,
    },
  ];

  return (
    <>
      <Dropdown
        menu={{ items }}
        trigger={['click']}
        placement="bottomRight"
      >
        <Space
          style={{
            cursor: 'pointer',
            padding: '4px 0',
            borderRadius: '4px',
            transition: 'background-color 0.2s',
          }}
          className="account-dropdown-trigger"
        >
          <Avatar
            style={{ backgroundColor: 'var(--rlc-surface-inverse)' }}
            size="default"
          >
            {currentUser?.first_name?.charAt(0)}{currentUser?.last_name?.charAt(0)}
          </Avatar>
          <span style={{ color: 'white', fontSize: '14px', fontWeight: 500 }}>
            {currentUser?.first_name}
          </span>
          <DownOutlined style={{ fontSize: '12px', color: 'white' }} />
        </Space>
      </Dropdown>

      {/* Modals */}
      <AccountSettingsModal
        open={accountSettingsOpen}
        onClose={() => setAccountSettingsOpen(false)}
      />
      <ChangePasswordModal
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
    </>
  );
};

export default AccountDropdown;
